import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { stockQtySchema } from "./inventory.js";
import { paisa } from "./money.js";
import { orderDetailSchema, orderLinesInputSchema } from "./orders.js";
/** Customer returns and exchanges (O-07, O-08; docs/specs/sales.md "B-34"). */
export const RETURN_KINDS = ["delivery_return", "customer_return", "exchange"];
export const RETURN_REASONS = [
    "damaged",
    "wrong_item",
    "not_as_described",
    "changed_mind",
    "late_delivery",
    "other",
];
export const RETURN_CONDITIONS = ["sellable", "damaged"];
/** How the money went back. `store_credit` lowers the customer's dues (credit ledger); `none` keeps it. */
export const REFUND_METHODS = ["none", "cash", "bkash", "nagad", "bank", "store_credit"];
export const returnKindSchema = z.enum(RETURN_KINDS);
export const returnReasonSchema = z.enum(RETURN_REASONS);
export const returnConditionSchema = z.enum(RETURN_CONDITIONS);
export const refundMethodSchema = z.enum(REFUND_METHODS);
const positiveQty = stockQtySchema.refine((value) => BigInt(value) > 0n, "Must be more than zero");
export const returnLineSchema = z.object({
    id: z.uuid(),
    orderLineId: z.uuid(),
    variantId: z.uuid(),
    name: z.string(),
    sku: z.string(),
    qty: stockQtySchema,
    unitPrecision: z.int(),
    condition: returnConditionSchema,
    /** The line's total, pro rata to the quantity back. */
    totalPaisa: paisa(),
});
export const returnSchema = z.object({
    id: z.uuid(),
    number: z.string(),
    kind: returnKindSchema,
    orderId: z.uuid(),
    orderNumber: z.string(),
    locationId: z.uuid(),
    reason: returnReasonSchema,
    /** What the goods were worth (sum of the lines). */
    totalPaisa: paisa(),
    refundMethod: refundMethodSchema,
    refundPaisa: paisa(),
    /** Exchange: the replacement order and its total minus the returned value (positive: the customer pays more). */
    exchangeOrderId: z.uuid().nullable(),
    differencePaisa: paisa(),
    note: z.string(),
    createdBy: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
});
export const returnDetailSchema = returnSchema.extend({ lines: z.array(returnLineSchema) });
export const returnListQuerySchema = z.object({
    orderId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    kind: returnKindSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
});
export const returnListSchema = z.object({ data: z.array(returnSchema), nextCursor: z.uuid().nullable() });
const returnLines = z
    .array(z
    .object({ lineId: z.uuid(), qty: positiveQty, condition: returnConditionSchema.default("sellable") })
    .strict())
    .min(1)
    .max(200)
    .refine((lines) => new Set(lines.map((l) => l.lineId.toLowerCase())).size === lines.length, "Each line once");
const returnFields = {
    lines: returnLines,
    reason: returnReasonSchema,
    /** Where the goods go back into stock; default the order's location. */
    locationId: z.uuid().optional(),
    note: z.string().trim().max(500).default(""),
};
/**
 * `POST /v1/orders/{id}/returns` (O-07): goods back from a delivered order. Sellable goods return to stock
 * (`sale_return`); damaged ones are returned and written off in the same step (`adjustment`, reason `damage`).
 * `refundPaisa` defaults to the returned value and may not exceed it.
 */
export const createReturnRequestSchema = z
    .object({
    ...returnFields,
    refundMethod: refundMethodSchema,
    refundPaisa: paisa().min(0).optional(),
})
    .strict();
/**
 * `POST /v1/orders/{id}/exchanges` (O-08): the return and a replacement order in one transaction. The returned value
 * pays for the replacement (its advance); what is left over is refunded by `refundMethod`, what is missing the
 * customer pays on the new order.
 */
export const createExchangeRequestSchema = z
    .object({
    ...returnFields,
    newLines: orderLinesInputSchema,
    refundMethod: refundMethodSchema.default("none"),
})
    .strict();
export const exchangeResultSchema = z.object({ return: returnDetailSchema, order: orderDetailSchema });
/** Outbox data for `sales.return.posted`. */
export const returnEventSchema = returnSchema.extend({
    lines: z.array(z.object({ variantId: z.uuid(), qty: stockQtySchema, condition: returnConditionSchema })),
});
/** Outbox data for `sales.receiptless_return.posted`: a POS return without a receipt has no order (B-50). */
export const receiptlessReturnEventSchema = returnEventSchema
    .omit({ orderId: true, orderNumber: true })
    .extend({ customerId: z.uuid().nullable() });
// Endpoints ----------------------------------------------------------------------------------------------------------
const tag = { tag: "sales" };
const manage = { permission: "returns.manage" };
export const createReturn = defineEndpoint({
    ...tag,
    name: "createReturn",
    method: "POST",
    path: "/v1/orders/{id}/returns",
    access: manage,
    summary: "Take goods back from a delivered order (sellable or damaged) and record the refund",
    params: idParamsSchema,
    body: createReturnRequestSchema,
    idempotent: true,
    status: 201,
    response: returnDetailSchema,
    errors: ["INVALID_TRANSITION", "OVER_RETURN", "LOCATION_INACTIVE", "REFUND_TOO_LARGE"],
});
export const createExchange = defineEndpoint({
    ...tag,
    name: "createExchange",
    method: "POST",
    path: "/v1/orders/{id}/exchanges",
    access: manage,
    summary: "Return goods and create the replacement order in one step; the price difference is handled",
    params: idParamsSchema,
    body: createExchangeRequestSchema,
    idempotent: true,
    status: 201,
    response: exchangeResultSchema,
    errors: [
        "INVALID_TRANSITION",
        "OVER_RETURN",
        "LOCATION_INACTIVE",
        "INSUFFICIENT_STOCK",
        "VARIANT_NOT_SELLABLE",
    ],
});
export const listReturns = defineEndpoint({
    ...tag,
    name: "listReturns",
    method: "GET",
    path: "/v1/returns",
    access: { permission: "orders.view" },
    summary: "Returns and exchanges at the caller's locations, newest first",
    query: returnListQuerySchema,
    status: 200,
    response: returnListSchema,
});
export const getReturn = defineEndpoint({
    ...tag,
    name: "getReturn",
    method: "GET",
    path: "/v1/returns/{id}",
    access: { permission: "orders.view" },
    summary: "One return with its lines",
    params: idParamsSchema,
    status: 200,
    response: returnDetailSchema,
});
//# sourceMappingURL=returns.js.map